import b from 'b_';
import memoizeOne from 'memoize-one';
import { MarkdownExtXContent, Spin, Text, Button, Modal } from 'platform-components';
import * as userSelectors from 'platform-components/src/components/user/selectors';
import { getMdStateFromProblem } from 'platform-components/src/utils/md-state';
import { mediaQueryResolver, withRouterPath, SCREEN_SIZE } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { bindActionCreators, compose } from 'redux';

import { LIBRARY_CONTENT_TYPE, BLOCK_CARDS_COUNT } from 'common.components/constants';
import { IconArrow } from 'common.components/icon';
import { actions as lessonActions } from 'common.components/lesson';
import { getCardBadges } from 'common.components/lesson/selectors';
import { CARD_BADGES } from 'common.components/stack/constants';
import * as userActions from 'common.components/user/actions';
import { actions as problemsViewActions } from 'store/problems-view/actions';
import * as problemsViewSelectors from 'store/problems-view/selectors';
import * as problemActions from 'store/problems/actions';
import * as problemSelectors from 'store/problems/selectors';
import { getProblemInfo } from 'store/problems/utils';

import BREAKPOINTS from '../../../../design/breakpoints.scss';
import * as actions from '../library.store/actions';
import { getProblemIdsInLesson } from '../library.store/selectors';

import StackModalHeader from './__header/stack-modal__header';
import { StackModalProblem } from './__problem/stack-modal__problem';
import { StackModalVariants } from './__variants/stack-modal__variants';
import { SCREEN_QUERY } from './typings';

import './stack-modal.scss';

export class StackModal extends React.Component {
    static propTypes = {
        addProblemToLesson: PropTypes.func.isRequired,
        canOpenNextStack: PropTypes.bool.isRequired,
        cardBadges: PropTypes.objectOf(PropTypes.oneOf(Object.values(CARD_BADGES))),
        clearEditor: PropTypes.func.isRequired,
        getProblemsRequest: PropTypes.func.isRequired,
        intl: PropTypes.object.isRequired,
        isCm: PropTypes.bool,
        isLoading: PropTypes.bool.isRequired,
        match: PropTypes.object.isRequired,
        onClose: PropTypes.func.isRequired,
        onStackChange: PropTypes.func.isRequired,
        problems: PropTypes.array.isRequired,
        problemIdsInLesson: PropTypes.array,
        removeProblem: PropTypes.func.isRequired,
        saveOrCreateLesson: PropTypes.func.isRequired,
        screenSize: PropTypes.oneOf(Object.values(SCREEN_SIZE)),
        selectStack: PropTypes.func.isRequired,
        setDeepLinkStack: PropTypes.func.isRequired,
        setWizardVariables: PropTypes.func.isRequired,
        stackIndex: PropTypes.number.isRequired,
        stack: PropTypes.object.isRequired,
        setSelectedProblemId: PropTypes.func.isRequired,
        selectedProblemId: PropTypes.number,
    };

    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };

    state = {
        shouldShowAnswers: false,
        tooltipShown: false,
        variantsCarouselVisible: false,
    };

    componentDidMount() {
        const {
            getProblemsRequest,
            setSelectedProblemId,
            stack: { id, representative },
        } = this.props;

        setSelectedProblemId(representative.id);
        getProblemsRequest(id);
    }

    componentDidUpdate({ stack: { id: prevStackId } }) {
        const {
            getProblemsRequest,
            setSelectedProblemId,
            stack: { id, representative },
        } = this.props;

        if (id !== prevStackId) {
            getProblemsRequest(id);

            setSelectedProblemId(representative.id);
        }
    }

    componentWillUnmount() {
        const { selectStack, setDeepLinkStack, setSelectedProblemId } = this.props;

        selectStack(null);
        setDeepLinkStack(null);
        setSelectedProblemId(null);
    }

    variantsCarouselRef = React.createRef();
    modalContentRef = React.createRef();

    getSelectedProblem = memoizeOne((problems, selectedProblemId) => {
        return problems.find((p) => p.id === selectedProblemId);
    });

    getShouldShowProblemInfo = memoizeOne((selectedProblem, isCm) => {
        const info = selectedProblem ? getProblemInfo(selectedProblem.problem) : {};

        return info.solution || info.publicSolution || (isCm && info.comment);
    });

    handleVariantsVisibilityChange = (isVisible) => {
        this.setState({ variantsCarouselVisible: isVisible });
    };

    scrollToVariants = () => {
        if (!this.variantsCarouselRef.current) {
            return;
        }

        this.variantsCarouselRef.current.scrollIntoView({
            behavior: 'smooth',
        });
    };

    handleAddProblem = () => {
        const {
            addProblemToLesson,
            intl,
            onClose,
            saveOrCreateLesson,
            setWizardVariables,
            stack,
            problems,
            problemIdsInLesson,
            selectedProblemId: problemId,
        } = this.props;

        if (problemIdsInLesson.length < BLOCK_CARDS_COUNT) {
            this.setState({ tooltipShown: false });

            const selectedProblem = problemId ? problems.find((p) => p.id === problemId).problem : stack.representative;

            addProblemToLesson(selectedProblem, stack.id);
            setWizardVariables({ addProblemButtonClicked: true });
            saveOrCreateLesson({
                intl,
                onSuccess: (clessonId) => this.sendAddProblemGoal(clessonId, selectedProblem.id),
            });

            onClose();
        } else {
            this.setState({ tooltipShown: true });
        }
    };

    sendAddProblemGoal = (clessonId, problemId) => {
        const {
            match: {
                params: { subject, themeId },
            },
            stack,
        } = this.props;

        ym('reachGoal', 'teacher_lesson_creation_card_add_item', {
            clessonId,
            problemId,
            stackId: stack.id,
            subject,
            themeId,
        });
    };

    handleSelectProblem = ({ id: problemId }) => {
        const {
            match: {
                params: { subject, themeId },
            },
            stack,
            setSelectedProblemId,
        } = this.props;
        setSelectedProblemId(problemId);

        ym('reachGoal', 'teacher_lesson_creation_card_another_option', {
            subject,
            themeId,
            stackId: stack.id,
            problemId,
        });
    };

    handleToggleAnswers = () => {
        const {
            stack: { id: stackId },
        } = this.props;
        this.setState({ shouldShowAnswers: !this.state.shouldShowAnswers });

        ym('reachGoal', 'teacher_lesson_creation_answers_tubmler', {
            contentType: LIBRARY_CONTENT_TYPE.STACK,
            stackId,
        });
    };

    deleteFromLesson = () => {
        const {
            match: {
                params: { themeId },
            },
            removeProblem,
            stack: { id: stackId },
            selectedProblemId,
        } = this.props;

        removeProblem({ problemId: selectedProblemId, stackId });

        ym('reachGoal', 'teacher_lesson_creation_card_delete_item', {
            themeId,
            stackId,
            problemId: selectedProblemId,
        });
    };

    openPrevStack = () => {
        const { onStackChange, stackIndex } = this.props;

        if (stackIndex !== 0) {
            this.callNavMetrika();
            onStackChange(stackIndex - 1);
        }
    };

    openNextStack = () => {
        const { onStackChange, stackIndex, canOpenNextStack } = this.props;

        if (canOpenNextStack) {
            this.callNavMetrika();
            onStackChange(stackIndex + 1);
        }
    };

    callNavMetrika = () => {
        const {
            match: {
                params: { subject, themeId },
            },
            stack: { id: stackId },
            selectedProblemId,
        } = this.props;

        ym('reachGoal', 'teacher_lesson_creation_card_carousel', {
            subject,
            themeId,
            stackId,
            problemId: selectedProblemId,
        });
    };

    // eslint-disable-next-line complexity
    renderProblemInfo = () => {
        const { isCm, problems, selectedProblemId } = this.props;

        const selectedProblem = this.getSelectedProblem(problems, selectedProblemId);
        const shouldShowProblemInfo = this.getShouldShowProblemInfo(selectedProblem, isCm);
        const info = selectedProblem ? getProblemInfo(selectedProblem.problem) : {};
        const mdState = selectedProblem ? getMdStateFromProblem(selectedProblem.problem) : null;

        if (!shouldShowProblemInfo) {
            return null;
        }

        return (
            <div className="stack-modal__info">
                {isCm && info.comment && (
                    <div className="stack-modal__info-item">
                        <Text
                            id="library.problemInfo.comment"
                            size={Text.SIZE.XL}
                            tagName={Text.TAG.P}
                            weight={Text.WEIGHT.L}
                        />
                        <Text size={Text.SIZE.XL}>
                            <MarkdownExtXContent mdState={mdState}>{info.comment}</MarkdownExtXContent>
                        </Text>
                    </div>
                )}
                {info.solution && (
                    <div className="stack-modal__info-item">
                        <Text
                            id="library.problemInfo.solution"
                            size={Text.SIZE.XL}
                            tagName={Text.TAG.P}
                            weight={Text.WEIGHT.L}
                        />
                        <Text size={Text.SIZE.XL}>
                            <MarkdownExtXContent mdState={mdState}>{info.solution}</MarkdownExtXContent>
                        </Text>
                    </div>
                )}
                {info.publicSolution && (
                    <div className="stack-modal__info-item">
                        <Text
                            id="library.problemInfo.publicSolution"
                            size={Text.SIZE.XL}
                            tagName={Text.TAG.P}
                            weight={Text.WEIGHT.L}
                        />
                        <Text size={Text.SIZE.XL}>
                            <MarkdownExtXContent mdState={mdState}>{info.publicSolution}</MarkdownExtXContent>
                        </Text>
                    </div>
                )}
            </div>
        );
    };

    // eslint-disable-next-line complexity
    render() {
        const {
            isCm,
            isLoading,
            onClose,
            stack,
            cardBadges,
            problems,
            stackIndex,
            canOpenNextStack,
            problemIdsInLesson,
            screenSize,
            selectedProblemId,
        } = this.props;

        const { tooltipShown } = this.state;

        const selectedProblem = this.getSelectedProblem(problems, selectedProblemId);
        const shouldShowProblemInfo = this.getShouldShowProblemInfo(selectedProblem, isCm);
        const canOpenPrevStack = stackIndex > 0;
        const hasVariants = problems.length > 1;
        const bottomPanelNeeded = shouldShowProblemInfo || hasVariants;
        const isMobile = screenSize === SCREEN_SIZE.SMALL;

        let selectedProblemIndex = problems.findIndex((p) => p.id === selectedProblemId);
        if (selectedProblemIndex === -1) {
            selectedProblemIndex = 0;
        }

        return (
            <Modal mix="stack-modal" onRequestClose={onClose} opened useScale={false}>
                <div className="stack-modal__wrapper">
                    <div className={b('stack-modal', 'prev', { disabled: !canOpenPrevStack })}>
                        <div className="stack-modal__fake-problem" />
                        <Button
                            className={b('stack-modal', 'nav', { prev: true })}
                            disabled={!canOpenPrevStack}
                            icon={<IconArrow direction={IconArrow.DIRECTIONS.LEFT} />}
                            onClick={this.openPrevStack}
                            rounded
                            view={Button.VIEW.RAISED}
                        />
                    </div>
                    <div className="stack-modal__current" ref={this.modalContentRef}>
                        <Modal.CloseButton className="stack-modal__close-button" />
                        <StackModalHeader
                            onAddToLesson={this.handleAddProblem}
                            onDeleteFromLesson={this.deleteFromLesson}
                            problemIdsInLesson={problemIdsInLesson}
                            problems={problems}
                            scrollToVariants={this.scrollToVariants}
                            selectedProblemId={selectedProblemId}
                            shouldShowVariantsButton={hasVariants}
                            stack={stack}
                            tooltipShown={tooltipShown}
                        />
                        {isLoading ? (
                            <Spin center />
                        ) : (
                            <div className="stack-modal__content">
                                <div
                                    className={b('stack-modal__selected-problem-wrapper', {
                                        'with-bottom-panel': bottomPanelNeeded,
                                    })}
                                >
                                    {selectedProblem ? (
                                        <StackModalProblem
                                            problemLink={selectedProblem}
                                            selectedProblemId={selectedProblemId}
                                        />
                                    ) : (
                                        <Spin center />
                                    )}
                                </div>
                                <StackModalVariants
                                    cardBadges={cardBadges}
                                    handleVariantsVisibilityChange={this.handleVariantsVisibilityChange}
                                    hasVariants={hasVariants}
                                    isMobile={isMobile}
                                    modalContentRef={this.modalContentRef}
                                    onCarouselRef={this.variantsCarouselRef}
                                    onSelectProblem={this.handleSelectProblem}
                                    problems={problems}
                                    selectedProblem={selectedProblem}
                                    selectedProblemId={selectedProblemId}
                                    selectedProblemIndex={selectedProblemIndex}
                                    stack={stack}
                                />

                                {this.renderProblemInfo()}
                            </div>
                        )}
                    </div>

                    <div className={b('stack-modal', 'next', { disabled: !canOpenNextStack })}>
                        <div className="stack-modal__fake-problem" />
                        <Button
                            className={b('stack-modal', 'nav', { next: true })}
                            disabled={!canOpenNextStack}
                            icon={<IconArrow direction={IconArrow.DIRECTIONS.RIGHT} />}
                            onClick={this.openNextStack}
                            rounded
                            view={Button.VIEW.RAISED}
                        />
                    </div>
                </div>
            </Modal>
        );
    }
}

const mapStateToProps = (storeState, { stack: { id } }) => ({
    selectedProblemId: problemsViewSelectors.getSelectedProblemId(storeState),
    problems: problemSelectors.getProblemsWithUsage(storeState, { stackId: id }),
    cardBadges: getCardBadges(storeState),
    problemIdsInLesson: getProblemIdsInLesson(storeState),
    isCm: userSelectors.getIsContentManager(storeState),
});

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators(
        {
            getProblemsRequest: problemActions.getProblemsRequest,
            addProblemToLesson: actions.addProblemToLesson,
            saveOrCreateLesson: lessonActions.saveOrCreateLesson,
            clearEditor: lessonActions.clearEditor,
            selectStack: actions.selectStack,
            setDeepLinkStack: actions.setDeepLinkStack,
            setWizardVariables: userActions.setWizardVariables,
            removeProblem: lessonActions.removeProblemFromLesson,
            setSelectedProblemId: problemsViewActions.setSelectedProblemId,
        },
        dispatch
    );
};

export default compose(
    injectIntl,
    withRouterPath,
    mediaQueryResolver(['mobileBreakpoint'], BREAKPOINTS, SCREEN_QUERY),
    connect(mapStateToProps, mapDispatchToProps)
)(StackModal);
